import { useEffect, useState, useRef } from "react";
import * as LucideIcons from "lucide-react";

interface Stat {
  icon?: string;
  icon_name?: string;
  value: number;
  suffix: string;
  label: string;
}

interface StatsSectionProps {
  stats?: Stat[];
}

const defaultStats: Stat[] = [
  { icon: "Users", value: 160, suffix: "+", label: "Happy Clients" },
  { icon: "Briefcase", value: 450, suffix: "+", label: "Projects Delivered" },
  { icon: "Share2", value: 780, suffix: "+", label: "Social Campaigns" },
  { icon: "Calendar", value: 7, suffix: "+", label: "Years of Operation" },
];

function AnimatedNumber({ value, suffix }: { value: number; suffix: string }) {
  const [current, setCurrent] = useState(0);
  const ref = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.5 }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!isVisible) return;

    const duration = 2000;
    const steps = 60;
    const increment = value / steps;
    let currentStep = 0;

    const timer = setInterval(() => {
      currentStep++;
      setCurrent(Math.min(Math.round(increment * currentStep), value));

      if (currentStep >= steps) {
        clearInterval(timer);
      }
    }, duration / steps);

    return () => clearInterval(timer);
  }, [isVisible, value]);

  return (
    <div ref={ref} className="font-display text-5xl md:text-6xl lg:text-7xl text-gradient-gold">
      {current}
      {suffix}
    </div>
  );
}

export function StatsSection({ stats }: StatsSectionProps) {
  const displayStats = stats && stats.length > 0 ? stats : defaultStats;

  return (
    <section className="py-24 bg-background relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] rounded-full bg-gradient-radial from-primary to-transparent" />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-12">
          {displayStats.map((stat, index) => {
            const iconName = stat.icon_name || stat.icon;
            // @ts-ignore
            const IconComponent = LucideIcons[iconName] || LucideIcons.HelpCircle;

            return (
              <div
                key={stat.label}
                className="text-center group"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-secondary flex items-center justify-center group-hover:bg-gradient-gold transition-all duration-300">
                  <IconComponent
                    size={28}
                    className="text-primary group-hover:text-primary-foreground transition-colors"
                  />
                </div>
                <AnimatedNumber value={stat.value} suffix={stat.suffix} />
                <p className="text-muted-foreground mt-3 font-medium tracking-wide">
                  {stat.label}
                </p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
